import { createFileRoute, redirect, Outlet } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Sidebar } from '@/components/layout/sidebar'
import { TopBar } from '@/components/layout/topbar'
import { Paywall } from '@/components/paywall'
import { useCompany } from '@/hooks/use-company'
import { subState } from '@/lib/subscription'
import { hasCompany } from '@/lib/account'

const SIDEBAR_STORAGE_KEY = 'myserbisyo.sidebar.v1'

function storedSidebarCompact() {
  if (typeof window === 'undefined') return false
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'compact'
  } catch {
    return false
  }
}

export const Route = createFileRoute('/_app')({
  beforeLoad: async () => {
    const { data } = await supabase.auth.getSession()
    if (!data.session) throw redirect({ to: '/login' })
    // Spec 002: the business tools are for trade persons. A client has no company, so every query
    // here would return nothing anyway; the guard sends them to their own side instead.
    if (!hasCompany(data.session)) throw redirect({ to: '/home' })
    // MFA gate: a session that has a verified factor but has only passed the
    // password step (aal1 while aal2 is reachable) may not enter the app.
    // Users without MFA report aal1/aal1 and pass straight through.
    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
    if (aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2') {
      throw redirect({ to: '/mfa-challenge' })
    }
  },
  component: AppLayout,
})

function AppLayout() {
  const { company, isLoading } = useCompany()
  const s = subState(company)
  const [navOpen, setNavOpen] = useState(false)
  const [sidebarCompact, setSidebarCompact] = useState(storedSidebarCompact)

  const toggleSidebar = () => {
    setSidebarCompact((current) => {
      const next = !current
      try { window.localStorage.setItem(SIDEBAR_STORAGE_KEY, next ? 'compact' : 'expanded') } catch { /* private browsing can deny storage */ }
      return next
    })
  }

  // The shortcut mirrors familiar desktop workspaces while leaving text entry untouched.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'b') return
      const target = event.target as HTMLElement | null
      if (target?.closest('input, textarea, select, [contenteditable="true"]')) return
      event.preventDefault()
      toggleSidebar()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  // Printed, the shell must grow to the full length of the page. On screen it is exactly one
  // screen tall and <main> scrolls inside it, so window.print() clipped every report to the first
  // screen: a five page run sheet printed as "1 of 1" (customer report).
  return (
    <div className="flex h-screen overflow-hidden bg-canvas text-token-primary print:block print:h-auto print:overflow-visible">
      <Sidebar compact={sidebarCompact} mobileOpen={navOpen} onClose={() => setNavOpen(false)} />
      {navOpen && <div className="fixed inset-0 z-30 bg-[var(--scrim)] lg:hidden" onClick={() => setNavOpen(false)} />}
      <div className="flex min-w-0 flex-1 flex-col print:block">
        <TopBar
          onMenu={() => setNavOpen(true)}
          onSidebarToggle={toggleSidebar}
          sidebarCompact={sidebarCompact}
        />
        <main className="flex-1 overflow-auto print:overflow-visible">
          {isLoading || !company ? (
            <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-token-muted" /></div>
          ) : s.hasAccess ? (
            <Outlet />
          ) : (
            <Paywall pastDue={s.pastDue} />
          )}
        </main>
      </div>
    </div>
  )
}
